import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import Stack from '@common/components/stack';
import { Button } from '@common/components/button';
import SearchBar from '@common/components/search-bar';
import FilterTabs from '@common/components/filter-tabs';
import Icon from '@common/components/icon';
import { useChatStore } from '@modules/chat/store/chatStore';
import NewConversationForm from '@modules/chat/forms/new-conversation';
import './styles.scss';
import TabsComponent from '@common/components/tabs';
import { ESearhFilterTypes, EConversationFilterTypes } from '@modules/chat/types/objects';

interface TopbarProps {
  basePath: string;
}

const Topbar = (props: TopbarProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  const {
    setSearch,
    search,
    searchFilter,
    selectedFilter,
    isNewConversationModalVisible,
    setNewConversationModalVisible,
    getPath,
    handleNewConversation,
    isSearchFocused,
    setIsSearchFocused,
    setCurrentConversation,
    setConversations,
    reset,
    abortFetchConversations,
  } = useChatStore();

  const filters = [
    { id: EConversationFilterTypes.ALL, label: t('chat:chat_filter_all') },
    { id: EConversationFilterTypes.UNREAD, label: t('chat:chat_filter_unread') },
    { id: EConversationFilterTypes.FAVORITES, label: t('chat:chat_filter_favorites') },
    { id: EConversationFilterTypes.GROUPS, label: t('chat:chat_filter_groups') }
  ];

  const tabs = [
    { id: ESearhFilterTypes.CHATS, label: t('chat:chat_search_filter_chats') },
    { id: ESearhFilterTypes.MESSAGES, label: t('chat:chat_search_filter_messages') },
  ];

  const handleFilterChange = (filterId: string) => {
    if (filterId === EConversationFilterTypes.ALL) {
      history.push(`${props.basePath}`);
    } else {
      history.push(`${props.basePath}/filter/${filterId}`);
    }
  };

  const handleSearchFilterChange = useCallback((tabIndex: number) => {
    const filter = tabs[tabIndex].id;
    const query = search ? `?search=${search}` : '';
    history.push(`${props.basePath}/filter/${filter}${query}`);
  }, [tabs, props.basePath, history, search]);


  const onSearchFocus = useCallback(() => {
    abortFetchConversations();
    setIsSearchFocused(true);
    if (search === '' || search === null) {
      history.push(`${props.basePath}`);
      setCurrentConversation(null);
      setConversations([]);
    }
  }, [search, setIsSearchFocused, props.basePath, history, setCurrentConversation, setConversations, abortFetchConversations]);

  const onSearchCancel = () => {
    setIsSearchFocused(false);
    history.push(`${props.basePath}`);
  };

  const handleSearchChange = useCallback((value: string | null) => {
    if (value === '' || value === null) {
      history.push(`${props.basePath}`);
      setIsSearchFocused(false);
    } else {
      setIsSearchFocused(!!value);
      history.push(`${props.basePath}/filter/${searchFilter}?search=${value}`);
    }
  }, [search, searchFilter, setSearch, setIsSearchFocused, props.basePath, history, setCurrentConversation, reset]);

  const searchPlaceholder = useMemo(() => {
    if (isSearchFocused) {
      return searchFilter === ESearhFilterTypes.MESSAGES ?
        t('chat:conversations_search_placeholder_messages') :
        t('chat:conversations_search_placeholder_chats');
    }
    return t('chat:conversations_search_placeholder');
  }, [t, isSearchFocused, searchFilter]);

  return (
    <div className="tw-w-full tw-border-b tw-border-[rgba(244, 244, 245, 1)]">
      <div className="tw-px-6 tw-pt-4">
        <Stack direction="column" gap={4}>
          {(!search && !isSearchFocused) && (
            <Stack direction="row" justify="between" align="center">
              <h2>{t('chat:conversations')}</h2>
              <NewConversationForm
                basePath={getPath()}
                show={isNewConversationModalVisible}
                onClose={() => setNewConversationModalVisible(false)}
                onShow={() => setNewConversationModalVisible(true)}
                onNewConversation={handleNewConversation}
              >
                <Button size="small" type="primary" className="custom-blue-button">
                  <Icon type="edit_pencil" />
                </Button>
              </NewConversationForm>
            </Stack>
          )}
          <Stack direction="row" align="start" justify="start">
            {isSearchFocused && <Button icon="arrow_left" onClick={onSearchCancel} />}
            <SearchBar
              onSearch={handleSearchChange}
              debounce
              placeholder={searchPlaceholder}
              defaultValue={search}
              clearable
              onFocus={onSearchFocus}
              filter={false}
            />
          </Stack>
        </Stack>
      </div>
      <hr className="tw-m-0" />
      {isSearchFocused || search ?
        <TabsComponent
          className="custom-filter-chat-tabs"
          onChange={handleSearchFilterChange}
          initialTabIndex={searchFilter === ESearhFilterTypes.MESSAGES ? 1 : 0}
        >
          {tabs.map((tab) => (
            <div key={tab.id} title={tab.label} />
          ))}
        </TabsComponent>
        : (
          <div className="tw-px-6 tw-pt-3">
            <Stack direction="column" gap={4} className="tw-mb-3">
              <FilterTabs
                options={filters}
                selected={selectedFilter}
                onChange={handleFilterChange}
              />
            </Stack>
          </div>
        )}
    </div>
  );
};

export default Topbar;
